import React from 'react';
import type { Product } from '@/types/product';
import { useIsMobile } from '@/hooks/use-mobile';
import ImageCarousel from './ImageCarousel';
import VariantSelector from './VariantSelector';
import QuantitySelector from './variant-selectors/QuantitySelector';
import { useCart } from '@/context/CartContext';
import { Button } from './ui/button';
import { useToast } from '@/hooks/use-toast';
import { Star, ArrowLeft, Ruler } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Link } from "react-router-dom";

interface ProductDetailsProps {
  product: Product;
}

const ProductDetails = ({
  product
}: ProductDetailsProps) => {
  console.log("ProductDetails: Rendering with product:", product);
  const isMobile = useIsMobile();
  const { addItem } = useCart();
  const { toast } = useToast();

  const defaultVariant = {
    size: "unique",
    colorName: "standard",
    colorHex: "#000000"
  };

  const [selectedVariant, setSelectedVariant] = React.useState(
    product.variants && product.variants.length > 0 
      ? product.variants[0] 
      : defaultVariant
  );

  const [quantity, setQuantity] = React.useState(1);

  const handleAddToCart = () => {
    console.log("ProductDetails: Adding to cart with variant and quantity:", selectedVariant, quantity);
    
    addItem({
      id: product.id,
      name: product.name,
      variant: selectedVariant,
      quantity: quantity,
      price: product.price,
      image: product.images[0] || ''
    });

    toast({
      title: "Produit ajouté",
      description: "Le produit a été ajouté à votre panier"
    });
  };

  const renderSizeGuide = () => {
    if (!product.size_guide) return null;
    
    const sizes = product.variants 
      ? [...new Set(product.variants.map(v => v.size))]
      : [];
      
    if (sizes.length === 0) return null;
    
    const dimensions = Object.keys(product.size_guide[sizes[0]]);

    return (
      <div className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Toutes les mesures sont en centimètres. Pour un meilleur ajustement, prenez vos mesures et comparez-les au tableau ci-dessous.
        </p>
        
        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold text-base">Taille</TableHead>
                  {dimensions.map(dim => (
                    <TableHead key={dim} className="font-semibold text-base capitalize">
                      {dim}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sizes.map(size => (
                  <TableRow key={size} className="hover:bg-muted/50 transition-colors">
                    <TableCell className="font-medium text-primary">{size}</TableCell>
                    {dimensions.map(dim => (
                      <TableCell 
                        key={`${size}-${dim}`}
                        className="text-center"
                      >
                        {product.size_guide?.[size]?.[dim]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="rounded-lg border p-4 bg-muted/30">
          <h4 className="font-medium mb-2">Comment prendre vos mesures ?</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Poitrine : Mesurez autour de la partie la plus large de votre poitrine</li>
            <li>• Taille : Mesurez autour de votre taille naturelle</li>
            <li>• Hanches : Mesurez autour de la partie la plus large de vos hanches</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderStars = (rating: number) => {
    return <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`h-4 w-4 md:h-5 md:w-5 ${star <= 5 ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />)}
      </div>;
  };

  const shouldShowSizeGuide = 
    product.has_size_guide && 
    product.size_guide && 
    Object.keys(product.size_guide).length > 0 &&
    product.has_sizes &&
    product.variants.some(variant => variant.size !== null);

  const hasSizes = product.has_sizes;
  const hasColors = product.has_colors;

  return <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" />
          Retour à la boutique
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="relative">
          <ImageCarousel images={product.images} />
        </div>
        
        <div className="space-y-8">
          <div>
            {product.categories.length > 0 && <div className="mb-4">
                {product.categories.map(category => <Badge key={category} variant="secondary" className="mr-2 mb-2">
                    {category}
                  </Badge>)}
              </div>}
            
            <h1 className="text-3xl font-bold mb-6">{product.name}</h1>

            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:divide-x sm:divide-gray-200">
              <p className="text-2xl font-semibold text-red-600 pr-0 sm:pr-6">
                {product.price.toFixed(2)} $
              </p>
              <div className="py-2 px-0 sm:px-6">
                <Badge variant="outline" className="text-green-600 border-green-600">
                  En stock
                </Badge>
              </div>
              <div className="flex items-center gap-2 pl-0 sm:pl-6">
                {renderStars(5)}
                <span className="text-sm text-gray-500">(5.0/5)</span>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <div className="prose prose-gray max-w-none mb-8 [&>p]:mb-6 [&>p:last-child]:mb-0" dangerouslySetInnerHTML={{
              __html: product.description
            }} />
          </div>

          <Separator />

          <div className="space-y-6">
            {shouldShowSizeGuide && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 mb-6"
                  >
                    <Ruler className="h-5 w-5" />
                    Guide des tailles
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] w-full sm:max-w-3xl sm:w-[90vw] p-4 sm:p-6 h-[80vh] max-h-[80vh]">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-semibold mb-4">Guide des tailles</DialogTitle>
                  </DialogHeader>
                  <div className="overflow-y-auto h-full px-1">
                    {renderSizeGuide()}
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {(hasSizes || hasColors) && (
              <VariantSelector
                variants={product.variants}
                selectedVariant={selectedVariant}
                onVariantChange={setSelectedVariant}
                hasColors={hasColors}
                hasSizes={hasSizes}
              />
            )}

            <QuantitySelector
              quantity={quantity}
              onQuantityChange={setQuantity}
            />

            <Button 
              onClick={handleAddToCart}
              className="bg-red-600 hover:bg-red-700 text-lg px-8 sm:px-12 py-5 sm:py-6 w-full sm:w-auto"
            >
              Ajouter au panier
            </Button>
          </div>
        </div>
      </div>
    </div>;
};

export default ProductDetails;
